import React, {useState, useEffect} from 'react';
import Button, {btnSize} from "../../../components/UI/Button/Button.ui";
import ShareOnMedia from "../../../components/UI/ShareOnMedia/ShareOnMedia.ui";
import ScoreChart from "../../../components/UI/Charts/ScoreChart.ui";

import {DQ_THEMES, MODULE_TYPE, QUIZ_TIME_MODES} from "../../../utils/constants";
import {getPercentileStr, getBadgeResultStr, getBadgeResultColorClass, getCpciBandLabelKey} from "../../../utils/utils";
import {useLocation} from "react-router-dom";
import {GET_APP_LOCALIZE_TEXT} from "../../../utils/dq_lc_service/lc_service";
import styled from "styled-components";
import CoursePreview from "./CoursePreview.comp";
import { useGlobalState } from "../../../context/global_state";

const ListItemDetail = (props) => {

    const { history, badge, selected, profile, global_average, national_average } = props;
    const { setProfile } = useGlobalState();

    const is_standard_score = badge.default_scoring && badge.default_scoring === 'raw' ? false : true;
    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];

    const [quizBtnText, setQuizBtnText] = useState('Take Test');
    const [badgeScore, setBadgeScore] = useState('0');
    const [badgePercentile, setBadgePercentile] = useState('');
    const [badgeAllScores, setBadgeAllScores] = useState([]);
    const [hideRetakeButton, setHideRetakeButton] = useState(false);

    // CPCI (Child Online Safety) detection — same check used in QuizTime.page.jsx:
    // const isCpci = Quiz?.assessment_type === 'cpci' || matchesCpci(badgeCode) || matchesCpci(resumeRefModuleCode);
    const CPCI_MODULE_CODE_MATCH = 'child_online_safety_children_assessment';
    const matchesCpci = (code) => typeof code === 'string' && code.toLowerCase().includes(CPCI_MODULE_CODE_MATCH);
    // The badge row is the org module document, so its catalogue is on it; the name match
    // stays for modules created before `assessment_type` existed.
    const isCpci = badge.assessment_type === 'cpci' || matchesCpci(badge.badge_code);
    // CPCI retake cool-down: retake stays hidden until this long after the last completed attempt.
    const CPCI_RETAKE_COOLDOWN_MS = 365 * 24 * 60 * 60 * 1000; // 1 year

    const getLastCpciAttemptTime = (curr_badge_state) => {
        const scores = Array.isArray(curr_badge_state.scores) ? curr_badge_state.scores : [];
        if (scores.length === 0) return null;
        const lastScore = scores.reduce((latest, s) => (
            !latest || (s.order_number || 0) > (latest.order_number || 0) ? s : latest
        ), null);
        return lastScore && lastScore.result_time ? new Date(lastScore.result_time).getTime() : null;
    };

    // QA override — same `?qa-mode=1` convention used in QuizTime.page.jsx, gated to non-prod envs,
    // lets testers see the CPCI retake button immediately instead of waiting out the cool-down.
    const qaModeRef = React.useRef(false);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const qa_mode_param = params.get('qa-mode');
        if (qa_mode_param === '1' && ['LOCAL', 'DEVELOP', 'STAGE'].includes(process.env.REACT_APP_NAME)) {
            qaModeRef.current = true;
        }
    }, []);

    useEffect( ()=>{
        const curr_badge_state = profile.badges[badge.badge_code];
        if (curr_badge_state) {
            if (curr_badge_state.in_progress) {
                setQuizBtnText(GET_APP_LOCALIZE_TEXT(pageLc, 'post_quiz_resume_test'))
            } else if (curr_badge_state.finished) {
                setQuizBtnText(GET_APP_LOCALIZE_TEXT(pageLc, 'dq_home__retake_test'));
                setBadgeScore(parseFloat(curr_badge_state.obtained_score).toFixed(2) + ''); // cast to string
                setBadgePercentile(getPercentileStr(curr_badge_state.obtained_score, curr_badge_state.percentile, pageLc));
                if (typeof curr_badge_state.scores === 'object' && curr_badge_state.scores.length > 0) {
                    setBadgeAllScores(curr_badge_state.scores);
                }
                // CPCI assessments are locked for a cool-down period after the last completed attempt.
                if (isCpci && !qaModeRef.current) {
                    const lastAttemptTime = getLastCpciAttemptTime(curr_badge_state);
                    const canRetake = lastAttemptTime !== null && (Date.now() - lastAttemptTime) >= CPCI_RETAKE_COOLDOWN_MS;
                    setHideRetakeButton(!canRetake);
                } else {
                    setHideRetakeButton(false);
                }
            }
        }
    }, [profile] )

    let componentClasses = `${'home-item-detail-container'} ${'home-item-detail-close'}`
    if (selected === badge.id) {
        componentClasses = `${'home-item-detail-container'} ${'home-item-detail-open'}`
    }

    const takeQuizAction = () => {
        const curr_badge_state = profile.badges[badge.badge_code];
        if (curr_badge_state) {
            if (curr_badge_state.in_progress) {
                history.push(`/take/quiz/${QUIZ_TIME_MODES.RESUME_MODE}/${encodeURIComponent(curr_badge_state.resume_ref)}`);
            } else {
                history.push(`/take/quiz/${QUIZ_TIME_MODES.START_MODE}/${encodeURIComponent(badge.badge_code)}`);
            }
        } else {
            history.push(`/take/quiz/${QUIZ_TIME_MODES.START_MODE}/${encodeURIComponent(badge.badge_code)}`);
        }
    }
    const takeCourseAction = () => {
        const identifier = badge.badge_code;
        if (badge.module_type && badge.module_type === MODULE_TYPE.LEARNING_PROGRAM) {
            // Sync active theme color into global profile so CourseTime uses same color as Home
            if (badge.theme_color) {
                setProfile({
                    ...profile,
                    active_theme_color: badge.theme_color,
                });
            }
            history.push(`/take/course/${identifier}`);
        } else {
            // toast warning
            console.warn("This badge is not a learning program, cannot take course.");
        }
    }

    const BodyContainer = styled.div`
        font-family: ${(props) => {
            switch (props.theme.name) {
                case DQ_THEMES.DEFAULT:
                    return `"${props.theme.fonts[0]}", "${props.theme.fonts[1]}"` ;
                case DQ_THEMES.NUS_THEME:
                    return `"${props.theme.fonts[4]}"`;
                default:
                    return '';
            }
        }};
        font-weight: ${(props) => {
            switch (props.theme.name) {
                case DQ_THEMES.DEFAULT:
                    return 700;
                case DQ_THEMES.NUS_THEME:
                    return 700;
                default:
                    return 600;
            }
        }};
    `
    return(
        <div className={componentClasses} style={{borderColor: badge.theme_color}}>
            {badge.module_type && badge.module_type === MODULE_TYPE.LEARNING_PROGRAM ? (
                    <div className="home-item-detail-header-section">
                        <BodyContainer className="home-item-detail-header-title-program">
                            <h2 className="program-preview-title">
                                {GET_APP_LOCALIZE_TEXT(pageLc, 'home__course_description')}
                            </h2>
                            <div>{badge.desc}</div>
                        </BodyContainer>
                    </div>
                ) : (
            <div className={'home-item-detail-header-section'}>
                <div className={'home-item-detail-header-score'}>
                    {
                        badgeScore && badgePercentile ? (
                            <>
                                <span className={getBadgeResultColorClass(badgeScore, is_standard_score)}>{badgeScore.split(".")[0]}</span>
                                <span className={`${getBadgeResultColorClass(badgeScore, is_standard_score)} header-score-pt`}>.{badgeScore.split(".")[1]}</span>
                                {/* COS: the band name (High / Moderate / Basic / Low), no percentile — same as the result page. */}
                                <div className="home-item-detail-header-score-desc">{isCpci ? GET_APP_LOCALIZE_TEXT(pageLc, getCpciBandLabelKey(badgeScore)) : getBadgeResultStr(badgeScore, true, is_standard_score, pageLc)}</div>
                                {!isCpci && <div className="score-group">{badgePercentile}</div>}
                            </>
                       ) : null
                    }
                </div>
                <BodyContainer className={'home-item-detail-header-title'}>
                    <div>{badge.desc}</div>
                </BodyContainer>
            </div>
            )}
            {
                (badge.module_type && badge.module_type === MODULE_TYPE.LEARNING_PROGRAM) ?
                    <CoursePreview
                        badge={badge}
                        pageLc={pageLc}
                    />
                    :
                    <div className={'home-item-detail-charts-section'}>
                        <ScoreChart
                            score={badgeScore}
                            all_scores={badgeAllScores}
                            default_scoring={badge.default_scoring}
                            scoreChange={-0.02}
                            showStats={false}
                            global_average={global_average}
                            national_average={national_average}
                        />
                    </div>
            }
            {
                (badge.module_type && badge.module_type === MODULE_TYPE.LEARNING_PROGRAM) ?
                    <div className={'home-item-detail-btn-section'}>
                        <div className={'home-item-detail-t-btn'}>
                            <Button
                                size={btnSize.sm}
                                title={GET_APP_LOCALIZE_TEXT(pageLc, 'home__start_course')}
                                action={takeCourseAction}
                                themeColor={badge.theme_color}
                            />
                        </div>
                    </div>
                    :
                    <div className={'home-item-detail-btn-section'}>
                        <ShareOnMedia
                            title={GET_APP_LOCALIZE_TEXT(pageLc, 'home__share_test')}
                            themeColor={badge.theme_color}
                        />
                        {!hideRetakeButton && (
                            <div className={'home-item-detail-t-btn'}>
                                <Button
                                    size={btnSize.sm}
                                    title={quizBtnText}
                                    action={takeQuizAction}
                                    themeColor={badge.theme_color}
                                />
                            </div>
                        )}

                    </div>
            }
        </div>
    )
}

export default ListItemDetail;